/**
 * Number formatting for the Supernova tab. Display only.
 *
 * The em dash is the ONLY thing that ever stands in for a number we don't have.
 * Nothing here defaults a missing value to 0 — a zero on this tab reads as a
 * fact about the member's wallet, and inventing one is the failure mode the
 * whole data layer is built to prevent (see supernovaChain.js).
 */

export const EMPTY = "—";

const isNum = (n) => typeof n === "number" && Number.isFinite(n);

/** Whole counts: NFTs owned, NFTs staked, slots. */
export const fmtCount = (n) => (isNum(n) ? n.toLocaleString("en-US") : EMPTY);

/** XP points, whole. XP is an in-app points balance with no monetary value. */
export const fmtXp = (n) =>
  isNum(n) ? Math.round(n).toLocaleString("en-US") : EMPTY;

/**
 * XP to the cent — used where the exact model figure matters (the stake
 * preview's headline). Two decimals is what the handoff's verified fixtures are
 * quoted to: 1 NFT · 3y → "101,388.89", 2 NFT · 90d → "9,166.67".
 */
export const fmtXpExact = (n) =>
  isNum(n)
    ? n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : EMPTY;

/** "Oct 25, 2026" — an unlock date. Epoch ms in, EMPTY when we don't have one. */
export const fmtDate = (ms) =>
  isNum(ms)
    ? new Date(ms).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : EMPTY;

/**
 * "1.0×" / "1.25×" — a lock's XP rate multiplier, never a rate of return.
 *
 * One decimal minimum so 2 reads as "2.0×", two maximum so the quarter-step
 * tiers stay exact: rounding 1.25 to "1.3×" would misquote the config's table
 * on the very screen a member picks a lock from.
 */
export const fmtMultiplier = (m) =>
  isNum(m)
    ? `${m.toLocaleString("en-US", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
      })}×`
    : EMPTY;

/**
 * "62.7%" — the early-exit DECAY PENALTY, and the only percentage this feature
 * ever renders.
 *
 * COMPLIANCE: it is the share of an in-app points balance a member gives up by
 * exiting a lock early. It is not a rate of return, a yield, an APR/APY, or any
 * figure about money — none of those may ever be added to this tab. It is shown
 * because hiding the size of a penalty from the person about to pay it would be
 * the worse failure.
 *
 * One decimal, matching the mockup, so a penalty near zero still reads as a real
 * number instead of rounding to "0%" on the last day of a lock.
 */
export const fmtPenaltyPct = (p) =>
  isNum(p)
    ? `${p.toLocaleString("en-US", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`
    : EMPTY;

/** "4 staked" / "1 staked" — pluralization without a library. */
export const plural = (n, one, many) => `${fmtCount(n)} ${n === 1 ? one : many}`;

/** 0x2EF0…35a2 */
export const shortAddr = (a) =>
  !a ? "" : a.length <= 14 ? a : `${a.slice(0, 6)}…${a.slice(-4)}`;
